import {
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useMemo,
} from "react";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { UserTypeEnum } from "@features/mySph/utils/types";
import {
  AdsProvider,
  createSubscriberTargetingProvider,
  PostitialClickGate,
  PrestitialGate,
} from "@sphtech/web2-ads";
import { ENVIRONMENT, IS_PLAYWRIGHT } from "@util/constant";
import {
  type BtSdkAdsCanaryConfig,
  getBtSdkAdsAdtagEnv,
} from "@util/sdkAdsConfig";

type BtSdkAdsShellProps = {
  config?: BtSdkAdsCanaryConfig;
} & PropsWithChildren;

function BtSdkAdsCanaryVersionLog({
  config,
}: {
  config: BtSdkAdsCanaryConfig;
}): null {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      `[bt-canary] commit=${__GIT_COMMIT__} appVersion=${__APP_VERSION__} url=${config.url}`
    );
  }, [config.url]);

  return null;
}

export default function BtSdkAdsShell({
  config,
  children,
}: BtSdkAdsShellProps): ReactElement {
  if (!config) return <>{children}</>;
  if (IS_PLAYWRIGHT) return <>{children}</>;

  return (
    <BtSdkAdsShellContent config={config}>
      {children ?? null}
    </BtSdkAdsShellContent>
  );
}

// Hooks are isolated in this inner component so they (and the
// `createSubscriberTargetingProvider` side-effect) never execute when the
// shell returns early for missing config or Playwright runs.
function BtSdkAdsShellContent({
  config,
  children,
}: Required<BtSdkAdsShellProps>): ReactElement {
  // mySPH login state is reactive and resolves asynchronously (anonymous on the
  // first paint, then updates once the store settles). Map it to the GAM
  // subscriber value here. `useUserInfo().userType` comes from `getUserType`,
  // which today only yields SUBSCRIBER / REGISTERED / ANONYMOUS — in-app
  // subscribers carry the `bt_iap` flag (in SUBSCRIBER_FLAGS), so they already
  // resolve to SUBSCRIBER ("Y"). The explicit IAP_SUBSCRIBER check is defensive
  // in case `getUserType` is later changed to surface that enum case directly.
  // `targetingProviderKey` re-applies the key when login lands; the provider
  // array is memoized so it does not re-resolve (and re-define slots) on every
  // render.
  const { userType } = useUserInfo();
  const subscriber =
    userType === UserTypeEnum.SUBSCRIBER ||
    userType === UserTypeEnum.IAP_SUBSCRIBER
      ? "Y"
      : userType === UserTypeEnum.REGISTERED
        ? "R"
        : "N";
  const targetingProviders = useMemo(
    () => [createSubscriberTargetingProvider(subscriber)],
    [subscriber]
  );

  const pageTargetingContext = {
    ...(config.articleId ? { articleId: config.articleId } : {}),
    ...(config.articleTags ? { articleTags: [...config.articleTags] } : {}),
    siteKey: config.siteKey,
    pageType: config.pageType,
  };

  return (
    <>
      <BtSdkAdsCanaryVersionLog config={config} />
      <AdsProvider
        siteKey={config.siteKey}
        mode="production"
        env={getBtSdkAdsAdtagEnv(ENVIRONMENT)}
        pageTargetingContext={pageTargetingContext}
        grapeshot={{ urlPath: config.url }}
        targetingProviders={targetingProviders}
        targetingProviderKey={subscriber}
      >
        <PostitialClickGate page={config.page} />
        <PrestitialGate page={config.page}>{children}</PrestitialGate>
      </AdsProvider>
    </>
  );
}
